import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Spin,
  Table,
  Typography,
  message,
} from 'antd';
import { ArrowLeftOutlined, DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import api from '@/services/api.service';
import { epcSubcontractAdminService } from '@/services/epcSubcontractAdmin.service';
import { useProjectsForSelection } from '@/hooks/useProject';
import { useCurrency } from '@/contexts/CurrencyContext';
import { uiPlainCurrencyLabel } from '@/constants/uaDirhamDisplay';
import { CostControlLineCell } from '@/components/project/CostControlLineCell';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import { formatMoney } from '@/utils/format';

const { Title, Text } = Typography;

type SupplierOption = {
  id: string;
  supplier_name?: string;
  supplier_code?: string;
  is_active?: boolean;
  supplier_type?: string;
  supplierType?: string;
};

type AgreementLineDraft = {
  key: string;
  lineCode: string;
  description: string;
  qty: number;
  unitRate: number;
  wbs_node_id: string | null;
  cbs_code_id: string | null;
  cost_category: string;
};

const PRICING_BASIS_OPTIONS = [
  { value: 'LumpSum', label: 'Lump sum' },
  { value: 'RemeasuredBOQ', label: 'Remeasured BOQ' },
  { value: 'Milestone', label: 'Milestone' },
  { value: 'CostPlus', label: 'Cost plus' },
];

const CLAUSE_PROFILE_OPTIONS = [
  { value: 'CUSTOM', label: 'Custom' },
  { value: 'FIDIC_RED_BOOK', label: 'FIDIC Red Book' },
  { value: 'FIDIC_YELLOW_BOOK', label: 'FIDIC Yellow Book' },
  { value: 'FIDIC_SILVER_BOOK', label: 'FIDIC Silver Book' },
  { value: 'FIDIC_GREEN_BOOK', label: 'FIDIC Green Book' },
  { value: 'CLIENT_STANDARD', label: 'Client standard' },
  { value: 'COMPANY_STANDARD', label: 'Company standard' },
];

function unwrapList<T>(res: unknown): T[] {
  const body = res as { data?: unknown };
  const data = body?.data ?? res;
  const inner = (data as { data?: unknown })?.data ?? data;
  return Array.isArray(inner) ? (inner as T[]) : [];
}

function apiErr(e: unknown): string {
  const ax = e as {
    response?: { data?: { message?: string; errors?: { message?: string } } };
    message?: string;
  };
  const data = ax?.response?.data;
  return data?.message || data?.errors?.message || ax?.message || 'Request failed';
}

function newLine(partial?: Partial<AgreementLineDraft>): AgreementLineDraft {
  return {
    key: `ln-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    lineCode: '',
    description: '',
    qty: 1,
    unitRate: 0,
    wbs_node_id: null,
    cbs_code_id: null,
    cost_category: 'Subcontract',
    ...partial,
  };
}

function lineAmount(ln: AgreementLineDraft): number {
  return Math.round(Number(ln.qty || 0) * Number(ln.unitRate || 0) * 100) / 100;
}

const EpcSubcontractAgreementFormPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form] = Form.useForm();
  const projectIdWatch = Form.useWatch('projectId', form) as string | undefined;
  const currencyWatch = Form.useWatch('currency', form) as string | undefined;
  const { baseCurrency, allCurrencies, loading: currenciesLoading } = useCurrency();
  const baseCode = baseCurrency?.currency_code ?? 'AED';
  const [lines, setLines] = useState<AgreementLineDraft[]>([newLine()]);

  const currencyOptions = useMemo(() => {
    const active = (allCurrencies || []).filter((c) => c.is_active);
    if (active.length > 0) {
      return active.map((c) => {
        const d = uiPlainCurrencyLabel(c.symbol, c.currency_code);
        const label =
          d === c.currency_code
            ? `${c.currency_code} (${c.currency_name})`
            : `${d} ${c.currency_code}`;
        return { value: c.currency_code, label };
      });
    }
    return [
      {
        value: baseCode,
        label: baseCurrency?.currency_name ? `${baseCode} (${baseCurrency.currency_name})` : baseCode,
      },
    ];
  }, [allCurrencies, baseCurrency, baseCode]);

  const { data: projects = [] } = useProjectsForSelection();
  const projectOptions = useMemo(
    () =>
      (projects || []).map((p) => ({
        value: p.id,
        label: p.project_number
          ? `${p.project_number} — ${p.project_name}`
          : p.project_name || p.id,
      })),
    [projects]
  );

  const suppliersQ = useQuery({
    queryKey: ['procurement-suppliers'],
    queryFn: async () => {
      const res = await api.get('/procurement/suppliers');
      return unwrapList<SupplierOption>(res);
    },
    staleTime: 60_000,
  });

  const supplierOptions = useMemo(() => {
    const list = Array.isArray(suppliersQ.data) ? suppliersQ.data : [];
    const active = list.filter((s) => s.is_active !== false);
    const typed = (s: SupplierOption) =>
      String(s.supplier_type || s.supplierType || '').toLowerCase();
    const subs = active.filter((s) => typed(s).includes('subcontract'));
    const preferred = subs.length
      ? [...subs, ...active.filter((s) => !typed(s).includes('subcontract'))]
      : active;
    return preferred.map((s) => ({
      value: s.id,
      label: s.supplier_code
        ? `${s.supplier_code} — ${s.supplier_name}`
        : s.supplier_name || s.id,
    }));
  }, [suppliersQ.data]);

  const agreementQ = useQuery({
    queryKey: ['epc-subcontract-agreement', id],
    queryFn: () => epcSubcontractAdminService.getAgreement(id!),
    enabled: isEdit,
  });

  const contractSum = useMemo(
    () => Math.round(lines.reduce((s, ln) => s + lineAmount(ln), 0) * 100) / 100,
    [lines]
  );

  useEffect(() => {
    if (!isEdit) {
      form.setFieldsValue({
        currency: baseCode,
        pricingBasis: 'LumpSum',
        nominated: false,
        payWhenPaid: false,
        clauseProfile: 'CUSTOM',
      });
      setLines([newLine()]);
      return;
    }
    const agr = agreementQ.data;
    if (!agr) return;
    const status = String(agr.status || '');
    if (status && status !== 'Draft' && status !== 'Rejected') {
      message.warning('Only Draft or Rejected agreements can be edited here');
      navigate(`/epc/commercial/subcontracts/${id}`, { replace: true });
      return;
    }
    const agrLines = Array.isArray(agr.lines) ? (agr.lines as Record<string, unknown>[]) : [];
    form.setFieldsValue({
      title: agr.title,
      supplierId: agr.supplier_id,
      projectId: agr.project_id || undefined,
      department_id: agr.department_id || undefined,
      pricingBasis: agr.pricing_basis || 'LumpSum',
      currency: agr.currency || baseCode,
      advancePct: agr.advance_pct != null ? Number(agr.advance_pct) : undefined,
      retentionPct: agr.retention_pct != null ? Number(agr.retention_pct) : undefined,
      attendancePct: agr.attendance_pct != null ? Number(agr.attendance_pct) : undefined,
      nominated: Boolean(agr.nominated),
      payWhenPaid: Boolean(agr.pay_when_paid),
      ldRatePerDay: agr.ld_rate_per_day != null ? Number(agr.ld_rate_per_day) : undefined,
      ldCap: agr.ld_cap != null ? Number(agr.ld_cap) : undefined,
      clauseProfile: agr.clause_profile || 'CUSTOM',
    });
    if (agrLines.length) {
      setLines(
        agrLines.map((ln, i) =>
          newLine({
            key: String(ln.id || `existing-${i}`),
            lineCode: String(ln.line_code || ''),
            description: String(ln.description || ''),
            qty: Number(ln.qty ?? 1) || 1,
            unitRate: Number(ln.unit_rate ?? ln.amount ?? 0) || 0,
            wbs_node_id: ln.wbs_node_id ? String(ln.wbs_node_id) : null,
            cbs_code_id: ln.cbs_code_id ? String(ln.cbs_code_id) : null,
            cost_category: String(ln.cost_category || 'Subcontract'),
          })
        )
      );
    } else {
      const sum = Number(agr.contract_sum) || 0;
      setLines([
        newLine({
          description: String(agr.title || 'Contract sum'),
          qty: 1,
          unitRate: sum,
          wbs_node_id: agr.wbs_node_id ? String(agr.wbs_node_id) : null,
          cbs_code_id: agr.cbs_code_id ? String(agr.cbs_code_id) : null,
          cost_category: String(agr.cost_category || 'Subcontract'),
        }),
      ]);
    }
  }, [isEdit, agreementQ.data, form, id, navigate, baseCode]);

  const updateLine = (key: string, patch: Partial<AgreementLineDraft>) => {
    setLines((prev) => prev.map((ln) => (ln.key === key ? { ...ln, ...patch } : ln)));
  };

  const removeLine = (key: string) => {
    setLines((prev) => (prev.length <= 1 ? prev : prev.filter((ln) => ln.key !== key)));
  };

  const saveM = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      if (isEdit) {
        return epcSubcontractAdminService.patchAgreement(id!, payload);
      }
      return epcSubcontractAdminService.createAgreement(payload);
    },
    onSuccess: (saved) => {
      message.success(isEdit ? 'Agreement updated' : 'Agreement created');
      qc.invalidateQueries({ queryKey: ['epc-subcontracts'] });
      const savedId = saved?.id ? String(saved.id) : id;
      if (savedId) {
        qc.invalidateQueries({ queryKey: ['epc-subcontract-workspace', savedId] });
        qc.invalidateQueries({ queryKey: ['epc-subcontract-agreement', savedId] });
        navigate(`/epc/commercial/subcontracts/${savedId}`);
      } else {
        navigate('/epc/commercial/subcontracts');
      }
    },
    onError: (e: unknown) => message.error(apiErr(e)),
  });

  const onFinish = (values: Record<string, unknown>) => {
    const validLines = lines
      .map((ln) => ({
        lineCode: ln.lineCode.trim() || undefined,
        description: ln.description.trim(),
        qty: Number(ln.qty) || 0,
        unitRate: Number(ln.unitRate) || 0,
        amount: lineAmount(ln),
        wbsNodeId: ln.wbs_node_id || undefined,
        cbsCodeId: ln.cbs_code_id || undefined,
        costCategory: ln.cost_category || 'Subcontract',
      }))
      .filter((ln) => ln.description && ln.amount > 0);

    if (!validLines.length) {
      message.error('Add at least one line with description and amount > 0');
      return;
    }

    const missingWbs =
      !!values.projectId &&
      validLines.some((ln) => !ln.wbsNodeId);

    // Soft warn only — cost control hard-gates on submit
    if (missingWbs) {
      message.warning(
        'Some lines have no WBS. Assign budget WBS before submit when cost control is active.'
      );
    }

    const sum = Math.round(validLines.reduce((s, ln) => s + ln.amount, 0) * 100) / 100;
    const headerWbs = validLines.find((ln) => ln.wbsNodeId)?.wbsNodeId;
    const headerCbs = validLines.find((ln) => ln.cbsCodeId)?.cbsCodeId;

    saveM.mutate({
      title: String(values.title || '').trim(),
      supplierId: values.supplierId,
      projectId: values.projectId || undefined,
      departmentId: values.department_id || undefined,
      pricingBasis: values.pricingBasis || 'LumpSum',
      currency: values.currency || baseCode,
      contractSum: sum,
      advancePct: values.advancePct != null ? Number(values.advancePct) : undefined,
      retentionPct: values.retentionPct != null ? Number(values.retentionPct) : undefined,
      attendancePct: values.attendancePct != null ? Number(values.attendancePct) : undefined,
      nominated: Boolean(values.nominated),
      payWhenPaid: Boolean(values.payWhenPaid),
      ldRatePerDay: values.ldRatePerDay != null ? Number(values.ldRatePerDay) : undefined,
      ldCap: values.ldCap != null ? Number(values.ldCap) : undefined,
      clauseProfile: values.clauseProfile || 'CUSTOM',
      wbsNodeId: headerWbs,
      cbsCodeId: headerCbs,
      costCategory: 'Subcontract',
      lines: validLines,
    });
  };

  if (isEdit && agreementQ.isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (isEdit && agreementQ.isError) {
    return (
      <div style={{ padding: 16 }}>
        <Alert
          type="error"
          showIcon
          message="Could not load agreement"
          description={(agreementQ.error as Error)?.message}
          action={
            <Button onClick={() => navigate('/epc/commercial/subcontracts')}>Back to register</Button>
          }
        />
      </div>
    );
  }

  const currencyLabel = currencyWatch || baseCode;

  return (
    <div style={{ padding: 16, maxWidth: 1100, margin: '0 auto' }}>
      <Space style={{ marginBottom: 16 }} wrap>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() =>
            navigate(isEdit ? `/epc/commercial/subcontracts/${id}` : '/epc/commercial/subcontracts')
          }
        >
          Back
        </Button>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            {isEdit ? 'Edit Subcontractor Agreement' : 'New Subcontractor Agreement'}
          </Title>
          <Text type="secondary">
            Code each activity line to project WBS/CBS for budget validation on submit and commitment
            on approve.
          </Text>
        </div>
      </Space>

      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          currency: baseCode,
          pricingBasis: 'LumpSum',
          nominated: false,
          payWhenPaid: false,
          clauseProfile: 'CUSTOM',
        }}
      >
        <Card title="Parties & scope" style={{ marginBottom: 16 }}>
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: 'Title is required' }]}
          >
            <Input placeholder="Agreement title" maxLength={200} />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                name="supplierId"
                label="Supplier / subcontractor"
                rules={[{ required: true, message: 'Supplier is required' }]}
                extra="Supplier must be Qualified / approved for award."
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  placeholder="Select supplier"
                  loading={suppliersQ.isLoading}
                  options={supplierOptions}
                  disabled={isEdit}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                name="projectId"
                label="Project"
                extra="Required for budget WBS pickers and cost-control commitment."
              >
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder="Select project"
                  options={projectOptions}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="department_id"
            label="Department"
            rules={[{ required: true, message: 'Department is required' }]}
            extra="Inherits from project when available; confirm or override."
          >
            <DepartmentSelect style={{ width: '100%', maxWidth: 480 }} />
          </Form.Item>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="pricingBasis" label="Pricing basis">
                <Select options={PRICING_BASIS_OPTIONS} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="clauseProfile" label="Clause profile">
                <Select options={CLAUSE_PROFILE_OPTIONS} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card title="Commercial terms" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={12} md={8}>
              <Form.Item
                name="currency"
                label="Currency"
                rules={[{ required: true, message: 'Select currency' }]}
              >
                <Select
                  showSearch
                  optionFilterProp="label"
                  loading={currenciesLoading}
                  options={currencyOptions}
                  placeholder="Select currency"
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={8}>
              <Form.Item label="Contract sum (from lines)">
                <Input
                  readOnly
                  value={formatMoney(contractSum, { currency: currencyLabel })}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={12} md={8}>
              <Form.Item name="advancePct" label="Advance %">
                <InputNumber min={0} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} md={8}>
              <Form.Item name="retentionPct" label="Retention %">
                <InputNumber min={0} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} md={8}>
              <Form.Item name="attendancePct" label="Attendance %">
                <InputNumber min={0} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
          <Space wrap size="large" style={{ marginBottom: 12 }}>
            <Form.Item name="nominated" valuePropName="checked" style={{ marginBottom: 0 }}>
              <Checkbox>Nominated subcontractor</Checkbox>
            </Form.Item>
            <Form.Item name="payWhenPaid" valuePropName="checked" style={{ marginBottom: 0 }}>
              <Checkbox>Pay when paid (commercial flag)</Checkbox>
            </Form.Item>
          </Space>
          <Row gutter={16}>
            <Col xs={12} md={8}>
              <Form.Item name="ldRatePerDay" label="LD rate / day">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={12} md={8}>
              <Form.Item name="ldCap" label="LD cap">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card
          title="Agreement lines (activities)"
          extra={
            <Button type="dashed" icon={<PlusOutlined />} onClick={() => setLines((p) => [...p, newLine()])}>
              Add line
            </Button>
          }
          style={{ marginBottom: 16 }}
        >
          {!projectIdWatch ? (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message="Select a project to assign WBS / CBS budget codes per line."
            />
          ) : null}
          <Table
            size="small"
            pagination={false}
            rowKey="key"
            dataSource={lines}
            scroll={{ x: 980 }}
            columns={[
              {
                title: 'Code',
                dataIndex: 'lineCode',
                width: 100,
                render: (_: unknown, row) => (
                  <Input
                    value={row.lineCode}
                    placeholder="CW-01"
                    onChange={(e) => updateLine(row.key, { lineCode: e.target.value })}
                  />
                ),
              },
              {
                title: 'Description',
                dataIndex: 'description',
                render: (_: unknown, row) => (
                  <Input.TextArea
                    autoSize={{ minRows: 1, maxRows: 3 }}
                    value={row.description}
                    placeholder="Activity / BOQ description"
                    onChange={(e) => updateLine(row.key, { description: e.target.value })}
                  />
                ),
              },
              {
                title: 'Qty',
                dataIndex: 'qty',
                width: 90,
                render: (_: unknown, row) => (
                  <InputNumber
                    min={0}
                    value={row.qty}
                    style={{ width: '100%' }}
                    onChange={(v) => updateLine(row.key, { qty: Number(v) || 0 })}
                  />
                ),
              },
              {
                title: 'Unit rate',
                dataIndex: 'unitRate',
                width: 120,
                render: (_: unknown, row) => (
                  <InputNumber
                    min={0}
                    value={row.unitRate}
                    style={{ width: '100%' }}
                    onChange={(v) => updateLine(row.key, { unitRate: Number(v) || 0 })}
                  />
                ),
              },
              {
                title: 'Amount',
                key: 'amount',
                width: 110,
                render: (_: unknown, row) => formatMoney(lineAmount(row), { currency: currencyLabel }),
              },
              {
                title: 'Budget (WBS/CBS)',
                key: 'cc',
                width: 180,
                render: (_: unknown, row) => (
                  <CostControlLineCell
                    projectId={projectIdWatch || null}
                    value={{
                      wbs_node_id: row.wbs_node_id,
                      cbs_code_id: row.cbs_code_id,
                      cost_category: row.cost_category || 'Subcontract',
                    }}
                    requestedAmount={lineAmount(row)}
                    onChange={(patch) =>
                      updateLine(row.key, {
                        wbs_node_id: patch.wbs_node_id ?? null,
                        cbs_code_id: patch.cbs_code_id ?? null,
                        cost_category: patch.cost_category || 'Subcontract',
                      })
                    }
                  />
                ),
              },
              {
                title: '',
                key: 'del',
                width: 48,
                render: (_: unknown, row) => (
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    disabled={lines.length <= 1}
                    onClick={() => removeLine(row.key)}
                  />
                ),
              },
            ]}
            summary={() => (
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={4}>
                  <Text strong>Contract sum</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4}>
                  <Text strong>{formatMoney(contractSum, { currency: currencyLabel })}</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5} colSpan={2} />
              </Table.Summary.Row>
            )}
          />
        </Card>

        <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
          <Link to={isEdit ? `/epc/commercial/subcontracts/${id}` : '/epc/commercial/subcontracts'}>
            Cancel
          </Link>
          <Space>
            <Button onClick={() => navigate('/epc/commercial/subcontracts')}>Back to register</Button>
            <Button type="primary" htmlType="submit" loading={saveM.isPending}>
              {isEdit ? 'Save changes' : 'Create agreement'}
            </Button>
          </Space>
        </Space>
      </Form>
    </div>
  );
};

export default EpcSubcontractAgreementFormPage;
